/**
 * One process flow — stages, activities, the reasoning and the stage
 * checklist. Management edits it in place; everyone else reads it and
 * confirms. Launches the narrated player.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Play, FileDown, CheckCircle2, Pencil, Save, Plus, Trash2,
  Users, Film, Mic, Upload, X, Zap, Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr } from '../../../components/admin/billing/shared';
import SopPlayer from './SopPlayer';

const field = 'w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:border-teal-500 focus:outline-none';

const toDraft = (flow) => ({
  title: flow.title || '',
  short_title: flow.short_title || '',
  subtitle: flow.subtitle || '',
  stages: (flow.stages || []).map((s) => ({
    id: s.id,
    title: s.title || '',
    role: s.role || '',
    purpose: s.purpose || '',
    activities: (s.activities || []).map((a) => ({ activity: a.activity || '', why: a.why || '' })),
    checklist_text: (s.checklist || []).join('\n'),
    narration: s.narration || '',
    audio_url: s.audio_url || '',
    audio_urls: s.audio_urls || {},
    video_url: s.video_url || '',
  })),
});

const SopFlowPage = () => {
  const { fid } = useParams();
  const { authHeader } = useAuth();
  const [flow, setFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [playing, setPlaying] = useState(false);
  const [draft, setDraft] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [langs, setLangs] = useState([{ code: 'en', label: 'English', native: 'English' }]);
  const [mediaLang, setMediaLang] = useState('en');
  const [prepLang, setPrepLang] = useState('en');
  const [job, setJob] = useState(null);

  // Warm-up: translate + narrate every stage up front so a training session
  // never waits on generation.
  const prepare = async () => {
    setBusy('prepare');
    try {
      const { data } = await axios.post(`${API}/sop/flows/${fid}/prepare`, {},
        { headers: authHeader(), params: { lang: prepLang } });
      setJob(data);
      toast.success(`Preparing all ${data.total} stages — you can leave this page`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  useEffect(() => {
    if (!job || job.status !== 'running') return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/sop/prepare/${job.id}`, { headers: authHeader() });
        setJob(data);
        if (data.status === 'done') {
          toast.success(data.failed
            ? `Prepared ${data.done} of ${data.total} stages — ${data.failed} failed`
            : `All ${data.done} stages are ready to play`);
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [job, authHeader]);

  useEffect(() => {
    axios.get(`${API}/sop/languages`, { headers: authHeader() })
      .then(({ data }) => setLangs(data.languages || []))
      .catch(() => {});
  }, [authHeader]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/sop/flows/${fid}`, { headers: authHeader() });
      setFlow(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, fid]);

  useEffect(() => { load(); }, [load]);

  const acknowledge = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await axios.post(`${API}/sop/flows/${fid}/acknowledge`, { kind },
        { headers: authHeader() });
      setFlow((p) => ({ ...p, my: { read_at: data.read_at, watched_at: data.watched_at } }));
      toast.success(kind === 'read' ? 'Recorded — read and understood' : 'Completion recorded');
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const pdf = async () => {
    setBusy('pdf');
    try {
      const res = await axios.get(`${API}/sop/flows/${fid}/pdf`,
        { headers: authHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const loadCompliance = async () => {
    setBusy('compliance');
    try {
      const { data } = await axios.get(`${API}/sop/flows/${fid}/compliance`,
        { headers: authHeader() });
      setCompliance(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const save = async () => {
    setBusy('save');
    try {
      const payload = {
        title: draft.title,
        short_title: draft.short_title,
        subtitle: draft.subtitle,
        stages: draft.stages.map((s) => ({
          id: s.id, title: s.title, role: s.role, purpose: s.purpose,
          activities: s.activities.filter((a) => (a.activity || '').trim()),
          checklist: (s.checklist_text || '').split('\n').map((c) => c.trim()).filter(Boolean),
          narration: s.narration, audio_url: s.audio_url, video_url: s.video_url,
        })),
      };
      const { data } = await axios.put(`${API}/sop/flows/${fid}`, payload, { headers: authHeader() });
      setFlow((p) => ({ ...p, ...data }));
      setDraft(null);
      toast.success('Process flow updated');
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const patchStage = (idx, key, value) => setDraft((p) => {
    const stages = [...p.stages];
    stages[idx] = { ...stages[idx], [key]: value };
    return { ...p, stages };
  });

  const patchActivity = (si, ai, key, value) => setDraft((p) => {
    const stages = [...p.stages];
    const activities = [...stages[si].activities];
    activities[ai] = { ...activities[ai], [key]: value };
    stages[si] = { ...stages[si], activities };
    return { ...p, stages };
  });

  const upload = async (stageId, file, lang = 'en') => {
    if (!file) return;
    setBusy(`upload-${stageId}`);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/sop/flows/${fid}/stages/${stageId}/upload`, fd,
        { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' }, params: { lang } });
      toast.success(data.kind === 'video' ? 'Clip attached to this stage' : 'Voiceover attached');
      await load();
      setDraft(null);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const clearMedia = async (stageId, kind) => {
    setBusy(`clear-${stageId}`);
    try {
      await axios.patch(`${API}/sop/flows/${fid}/stages/${stageId}/media`,
        kind === 'video' ? { video_url: '' } : { audio_url: '' }, { headers: authHeader() });
      toast.success('Removed');
      await load();
      setDraft(null);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  if (loading) {
    return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-teal-600" /></div>;
  }
  if (!flow) {
    return <div className="p-4 text-sm text-slate-600">Flow not found.</div>;
  }

  const stages = flow.stages || [];

  return (
    <div className="space-y-5 pb-24" data-testid="sop-flow-page">
      {playing && (
        <SopPlayer
          flow={flow}
          onClose={() => setPlaying(false)}
          onFinished={() => acknowledge('watched')}
        />
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/sop" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-1" data-testid="back-to-library">
            <ArrowLeft size={14} /> SOP library
          </Link>
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900">{flow.title}</h1>
          <p className="text-sm text-slate-500 mt-0.5 max-w-2xl">{flow.subtitle}</p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
            <span className="rounded-full bg-slate-100 text-slate-600 px-2 py-0.5">
              {stages.length} stages · v{flow.version}
            </span>
            {flow.my?.read_at && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5" data-testid="my-read-badge">
                <CheckCircle2 size={11} /> Read {flow.my.read_at.slice(0, 10)}
              </span>
            )}
            {flow.my?.watched_at && (
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 text-violet-700 px-2 py-0.5" data-testid="my-watched-badge">
                <Play size={11} /> Watched {flow.my.watched_at.slice(0, 10)}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setPlaying(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-teal-700"
            data-testid="play-sop-btn"
          >
            <Play size={15} /> Play narrated
          </button>
          <button
            onClick={pdf} disabled={busy === 'pdf'}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            data-testid="sop-pdf-btn"
          >
            {busy === 'pdf' ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} PDF
          </button>
          {flow.is_reviewer && (
            <button
              onClick={loadCompliance} disabled={busy === 'compliance'}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              data-testid="compliance-btn"
            >
              {busy === 'compliance' ? <Loader2 size={15} className="animate-spin" /> : <Users size={15} />} Who has read it
            </button>
          )}
          {flow.can_edit && (
            <div className="flex items-center gap-1.5">
              <select
                value={prepLang} onChange={(e) => setPrepLang(e.target.value)}
                className="rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm text-slate-700 focus:border-teal-500 focus:outline-none"
                data-testid="prepare-lang"
              >
                {langs.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.code === 'en' ? l.label : `${l.native} (${l.label})`}
                  </option>
                ))}
              </select>
              <button
                onClick={prepare} disabled={busy === 'prepare' || job?.status === 'running'}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                title="Generate and cache every stage before a training session"
                data-testid="prepare-flow-btn"
              >
                {busy === 'prepare' || job?.status === 'running'
                  ? <Loader2 size={15} className="animate-spin" /> : <Zap size={15} />}
                Prepare all stages
              </button>
            </div>
          )}
          {flow.can_edit && !draft && (
            <button
              onClick={() => setDraft(toDraft(flow))}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              data-testid="edit-flow-btn"
            >
              <Pencil size={15} /> Edit
            </button>
          )}
        </div>
      </div>

      {!draft && (
        <div className="bg-teal-50/60 border border-teal-200 rounded-xl p-3.5 flex flex-wrap items-center gap-3">
          <p className="text-sm text-slate-700 flex-1 min-w-[240px]">
            {flow.my?.read_at
              ? 'You have confirmed this flow. Re-confirm any time the process changes.'
              : 'Read the flow, then confirm you have understood it. Your supervisor sees this on your review sheet.'}
          </p>
          <button
            onClick={() => acknowledge('read')} disabled={busy === 'read'}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3.5 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:bg-slate-400"
            data-testid="acknowledge-read-btn"
          >
            {busy === 'read' ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
            {flow.my?.read_at ? 'Confirm again' : 'I have read and understood'}
          </button>
        </div>
      )}

      {job && (
        <div className="bg-white border border-slate-200 rounded-xl p-3.5" data-testid="prepare-panel">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-slate-800">
              {job.status === 'running' ? 'Preparing narration' : 'Narration ready'}
              {' · '}
              <span className="text-slate-500">
                {(langs.find((l) => l.code === job.lang) || {}).label || job.lang}
              </span>
              {' · '}
              <b data-testid="prepare-progress">{job.done}/{job.total}</b> stages
              {job.failed > 0 && <span className="text-red-600"> · {job.failed} failed</span>}
            </p>
            <button onClick={() => setJob(null)} className="text-slate-400 hover:text-slate-700" data-testid="close-prepare">
              <X size={16} />
            </button>
          </div>
          <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-teal-500 transition-all duration-500"
              style={{ width: `${Math.round(((job.done + job.failed) / Math.max(1, job.total)) * 100)}%` }}
            />
          </div>
          {job.message && <p className="mt-1.5 text-[11px] text-red-600">{job.message}</p>}
          {job.status === 'running' && (
            <p className="mt-1.5 text-[11px] text-slate-400">
              English takes about 20 seconds a stage; Bengali, Hindi and Kannada take about
              a minute each because they use the higher-quality native voice. You can leave
              this page — it keeps running.
            </p>
          )}
        </div>
      )}

      {compliance && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="compliance-panel">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h2 className="text-sm font-semibold text-slate-800">
              Read {compliance.read_count}/{compliance.total} · Watched {compliance.watched_count}/{compliance.total}
            </h2>
            <button onClick={() => setCompliance(null)} className="text-slate-400 hover:text-slate-700" data-testid="close-compliance">
              <X size={16} />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-slate-500 text-left">
                  <th className="pb-2">Name</th><th className="pb-2">Role</th>
                  <th className="pb-2">Read</th><th className="pb-2">Watched</th>
                </tr>
              </thead>
              <tbody>
                {compliance.rows.map((r) => (
                  <tr key={r.staff_id} className="border-t border-slate-100" data-testid={`compliance-row-${r.staff_id}`}>
                    <td className="py-2 text-slate-900">{r.name}</td>
                    <td className="py-2 text-slate-500">{r.role || '—'}</td>
                    <td className="py-2">{r.read_at
                      ? <span className="text-emerald-700">{r.read_at.slice(0, 10)}</span>
                      : <span className="text-red-600">Pending</span>}</td>
                    <td className="py-2">{r.watched_at
                      ? <span className="text-violet-700">{r.watched_at.slice(0, 10)}</span>
                      : <span className="text-slate-400">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {draft ? (
        <div className="space-y-4" data-testid="sop-edit-form">
          <div className="bg-white border border-slate-200 rounded-xl p-4 grid gap-3 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Flow title</label>
              <input className={`mt-0.5 ${field}`} value={draft.title}
                onChange={(e) => setDraft((p) => ({ ...p, title: e.target.value }))}
                data-testid="edit-flow-title" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Short name</label>
              <input className={`mt-0.5 ${field}`} value={draft.short_title}
                onChange={(e) => setDraft((p) => ({ ...p, short_title: e.target.value }))}
                data-testid="edit-flow-short" />
            </div>
            <div className="sm:col-span-3">
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Subtitle</label>
              <input className={`mt-0.5 ${field}`} value={draft.subtitle}
                onChange={(e) => setDraft((p) => ({ ...p, subtitle: e.target.value }))}
                data-testid="edit-flow-subtitle" />
            </div>
          </div>

          {draft.stages.map((s, si) => (
            <div key={s.id} className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid={`edit-stage-${si}`}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-teal-700">Stage {si + 1}</span>
                <button
                  onClick={() => setDraft((p) => ({ ...p, stages: p.stages.filter((_, x) => x !== si) }))}
                  className="ml-auto p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                  title="Remove stage" data-testid={`remove-stage-${si}`}
                >
                  <Trash2 size={15} />
                </button>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="sm:col-span-2">
                  <label className="text-[11px] uppercase tracking-wider text-slate-500">Stage title</label>
                  <input className={`mt-0.5 ${field}`} value={s.title}
                    onChange={(e) => patchStage(si, 'title', e.target.value)}
                    data-testid={`stage-title-${si}`} />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500">Role accountable</label>
                  <input className={`mt-0.5 ${field}`} value={s.role}
                    onChange={(e) => patchStage(si, 'role', e.target.value)}
                    data-testid={`stage-role-${si}`} />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500">Purpose</label>
                <input className={`mt-0.5 ${field}`} value={s.purpose}
                  onChange={(e) => patchStage(si, 'purpose', e.target.value)}
                  data-testid={`stage-purpose-${si}`} />
              </div>

              <div className="space-y-2">
                <label className="text-[11px] uppercase tracking-wider text-slate-500">Activities & why</label>
                {s.activities.map((a, ai) => (
                  <div key={`${si}-${ai}`} className="grid gap-2 sm:grid-cols-2">
                    <input className={field} value={a.activity} placeholder="Activity"
                      onChange={(e) => patchActivity(si, ai, 'activity', e.target.value)}
                      data-testid={`stage-${si}-activity-${ai}`} />
                    <div className="flex gap-2">
                      <input className={field} value={a.why} placeholder="Why we do it"
                        onChange={(e) => patchActivity(si, ai, 'why', e.target.value)}
                        data-testid={`stage-${si}-why-${ai}`} />
                      <button
                        onClick={() => patchStage(si, 'activities', s.activities.filter((_, x) => x !== ai))}
                        className="shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                        data-testid={`remove-activity-${si}-${ai}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => patchStage(si, 'activities', [...s.activities, { activity: '', why: '' }])}
                  className="inline-flex items-center gap-1.5 text-xs text-teal-700 hover:underline"
                  data-testid={`add-activity-${si}`}
                >
                  <Plus size={13} /> Add activity
                </button>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500">Checklist — one per line</label>
                <textarea rows={Math.min(12, (s.checklist_text || '').split('\n').length + 1)}
                  className={`mt-0.5 ${field}`} value={s.checklist_text}
                  onChange={(e) => patchStage(si, 'checklist_text', e.target.value)}
                  data-testid={`stage-checklist-${si}`} />
              </div>

              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-[11px] uppercase tracking-wider text-slate-500">Media for this stage</p>
                  <select
                    value={mediaLang} onChange={(e) => setMediaLang(e.target.value)}
                    className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:border-teal-500 focus:outline-none"
                    data-testid={`media-lang-${si}`}
                  >
                    {langs.map((l) => (
                      <option key={l.code} value={l.code}>
                        Voiceover language: {l.code === 'en' ? l.label : `${l.native} (${l.label})`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <label className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 cursor-pointer hover:bg-slate-50">
                    {busy === `upload-${s.id}` ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                    Upload clip, voiceover or photo
                    <input
                      type="file" className="hidden"
                      accept="video/mp4,video/quicktime,video/webm,audio/mpeg,audio/mp4,audio/wav,image/jpeg,image/png,image/webp"
                      onChange={(e) => upload(s.id, e.target.files?.[0], mediaLang)}
                      data-testid={`stage-upload-${si}`}
                    />
                  </label>
                  {s.video_url && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 text-sky-700 px-2 py-1">
                      <Film size={12} /> Clip attached
                      <button onClick={() => clearMedia(s.id, 'video')} className="hover:text-red-600" data-testid={`clear-video-${si}`}>
                        <X size={12} />
                      </button>
                    </span>
                  )}
                  {Object.keys(s.audio_urls || {}).map((code) => (
                    <span key={code} className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 px-2 py-1">
                      <Mic size={12} /> {(langs.find((l) => l.code === code) || {}).label || code} voiceover
                    </span>
                  ))}
                  {s.audio_url && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 px-2 py-1">
                      <Mic size={12} /> English voiceover
                      <button onClick={() => clearMedia(s.id, 'audio')} className="hover:text-red-600" data-testid={`clear-audio-${si}`}>
                        <X size={12} />
                      </button>
                    </span>
                  )}
                  {s.illustration_url && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-50 text-teal-700 px-2 py-1">
                      <ImageIcon size={12} /> Illustration set
                    </span>
                  )}
                  {!s.video_url && !s.audio_url && !s.illustration_url && (
                    <span className="text-slate-500">No media — the player narrates this stage.</span>
                  )}
                </div>
                <p className="text-[11px] text-slate-400">
                  mp4/mov clip up to 120 MB, an mp3/m4a/wav voiceover recorded on a phone, or a
                  jpg/png photo of your own bay to replace this stage's illustration.
                  The player already narrates every language in the list — upload a recording
                  only where you want a native-sounding voice instead.
                </p>
              </div>
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={save} disabled={busy === 'save'}
              className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:bg-slate-300"
              data-testid="save-flow-btn"
            >
              {busy === 'save' ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save flow
            </button>
            <button
              onClick={() => setDraft((p) => ({
                ...p,
                stages: [...p.stages, {
                  id: undefined, title: 'New stage', role: '', purpose: '',
                  activities: [{ activity: '', why: '' }], checklist_text: '',
                  narration: '', audio_url: '', video_url: '',
                }],
              }))}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              data-testid="add-stage-btn"
            >
              <Plus size={15} /> Add stage
            </button>
            <button onClick={() => setDraft(null)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50" data-testid="cancel-edit-btn">
              Cancel
            </button>
            <span className="text-[11px] text-slate-400">
              Saving keeps the narration in sync — a rewritten stage is re-narrated on
              its next play.
            </span>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {stages.map((s) => (
            <div key={s.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`stage-${s.order}`}>
              <div className="flex flex-wrap items-center gap-2 bg-slate-50 border-b border-slate-200 px-4 py-2.5">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-600 text-xs font-semibold text-white">
                  {s.order}
                </span>
                <h2 className="text-base font-semibold text-slate-900">{s.title}</h2>
                {s.role && (
                  <span className="ml-auto text-[11px] uppercase tracking-wider text-teal-700 font-semibold">
                    {s.role}
                  </span>
                )}
                {s.video_url && <Film size={14} className="text-sky-600" title="Filmed clip attached" />}
                {s.audio_url && <Mic size={14} className="text-amber-600" title="Voiceover attached" />}
              </div>
              <div className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  {s.illustration_url && (
                    <img
                      src={s.illustration_url} alt=""
                      className="w-full sm:w-52 shrink-0 rounded-xl border border-slate-200 object-cover aspect-[3/2]"
                      data-testid={`stage-illustration-${s.order}`}
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    {s.purpose && <p className="text-sm text-slate-600">{s.purpose}</p>}
                  </div>
                </div>
                <div className="mt-3 divide-y divide-slate-100">
                  {(s.activities || []).map((a, idx) => (
                    <div key={`${idx}-${a.activity}`} className="grid gap-1 sm:grid-cols-2 py-2.5">
                      <p className="text-sm text-slate-900">{a.activity}</p>
                      <p className="text-sm text-slate-500">{a.why}</p>
                    </div>
                  ))}
                </div>
                {(s.checklist || []).length > 0 && (
                  <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3">
                    <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">Checklist</p>
                    <ul className="grid gap-1.5 sm:grid-cols-2">
                      {s.checklist.map((c, idx) => (
                        <li key={`${idx}-${c}`} className="flex gap-2 text-sm text-slate-700">
                          <span className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border border-slate-400" />
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SopFlowPage;
